import { type FC, useMemo } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { ModuleReviewLayout } from "@/pages/postloss/modules/components/ModuleReviewLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { EstateItem } from "@/shared/types/estateTypes";

const OTHER_DEBT_TYPES = [
  "Co-signed loan or lease",
  "Unpaid rent",
  "Pending lawsuits",
  "Unfiled tax returns",
  "Tax payment plan",
  "State estate or inheritance taxes",
];

export const OtherDebtsReview: FC<FlowCustomComponentProps> = ({ onComplete, goToScreen }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const firstName = person?.first_name ?? "their";
  const { debts, isLoading, updateItem, isUpdatingItem, deleteItem, isDeletingItem } =
    useEstateItemActions(estateId);

  const sections = useMemo(() => {
    const grouped: Record<string, EstateItem[]> = {};
    for (const item of debts) {
      if (item.item_type && OTHER_DEBT_TYPES.includes(item.item_type)) {
        if (!grouped[item.item_type]) grouped[item.item_type] = [];
        grouped[item.item_type].push(item);
      }
    }
    return OTHER_DEBT_TYPES.filter((type) => grouped[type]?.length).map((type) => ({
      label: type,
      items: grouped[type],
    }));
  }, [debts]);

  return (
    <>
      <div className="text-center">
        <h2>Review {firstName}&apos;s other debts</h2>
        <p className="mt-2 text-secondary">
          Here&apos;s what we have. You can always come back to add or edit.
        </p>
      </div>
      <ModuleReviewLayout
        sections={sections}
        isLoading={isLoading}
        onComplete={() => onComplete()}
        onAdd={() => goToScreen("other_debts_checklist")}
        addLabel="Add a debt"
        showAddButton
        estateId={estateId}
        updateItem={updateItem}
        isUpdatingItem={isUpdatingItem}
        deleteItem={deleteItem}
        isDeletingItem={isDeletingItem}
      />
    </>
  );
};
